import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import ClockBoard from '../time/ClockBoard';
import {
  IcoClock, IcoCalendar, IcoDollar, IcoUsers, IcoCheckCircle,
  IcoFileText, IcoZap, IcoClipboard, IcoPlus, IcoChevronRight,
  IcoCheck, IcoX,
} from './DashIcons';
import '../../styles/admin-dashboard.css';

function ManagerDashboard() {
  const { user } = useAuth();
  const { features } = useOrgSettings();
  const navigate = useNavigate();

  const [employees, setEmployees] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = user?.token;
  const authHeaders = useMemo(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }), [token]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [empRes, entryRes] = await Promise.all([
          fetch('/api/staff/department', { headers: authHeaders }),
          fetch('/api/timeEntries/department', { headers: authHeaders })
        ]);
        if (empRes.ok) setEmployees(await empRes.json());
        if (entryRes.ok) setEntries(await entryRes.json());
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [authHeaders]);

  const thisWeekEntries = entries.filter(e => {
    if (!e?.date) return false;
    const entryDate = new Date(e.date);
    const today = new Date();
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay());
    return entryDate >= weekStart;
  });

  const totalHours = entries.reduce((sum, e) => sum + (Number(e.hoursWorked) || 0), 0);
  const pendingEntries = entries.filter(e => e.status === 'pending').length;

  const StatusBadge = ({ status }) => {
    const styles = {
      approved: { bg: '#d1fae5', color: '#065f46' },
      rejected: { bg: '#fee2e2', color: '#991b1b' },
      pending:  { bg: '#fef3c7', color: '#92400e' },
    };
    const s = styles[status] || styles.pending;
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '3px',
        padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: '700',
        background: s.bg, color: s.color,
      }}>
        {status === 'approved' && <IcoCheck size={11} color={s.color}/>}
        {status === 'rejected' && <IcoX size={11} color={s.color}/>}
        {status === 'pending'  && <IcoClock size={11} color={s.color}/>}
        {status === 'approved' ? 'Approved' : status === 'rejected' ? 'Rejected' : 'Pending'}
      </span>
    );
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-content">

        {/* Welcome */}
        <section className="welcome-section">
          <h1>Hello {user.firstName} {user.lastName}</h1>
          <p className="subtitle">What would you like to do next?</p>
        </section>

        {/* Live team clock board — only shown when clockInOut feature is enabled */}
        {features.clockInOut && <ClockBoard />}

        {/* Quick Action Buttons — matches admin style */}
        <div className="mobile-quick-actions" style={{ gridTemplateColumns: `repeat(${1 + (features.scheduling ? 1 : 0) + (features.payroll ? 1 : 0)}, 1fr)` }}>
          <button className="mobile-quick-action-btn time-entry" onClick={() => navigate('/time-cards')}>
            <span className="btn-icon"><IcoClock size={18} color="currentColor"/></span>
            Add Time Entry
          </button>
          {features.scheduling && (
            <button className="mobile-quick-action-btn schedule" onClick={() => navigate('/schedule')}>
              <span className="btn-icon"><IcoCalendar size={18} color="currentColor"/></span>
              Schedule
            </button>
          )}
          {features.payroll && (
            <button className="mobile-quick-action-btn payroll" onClick={() => navigate('/payroll')}>
              <span className="btn-icon"><IcoDollar size={18} color="currentColor"/></span>
              Run Payroll
            </button>
          )}
        </div>

        <div className="dashboard-grid">
          {/* Left Column */}
          <div className="left-column">

            {/* Stats */}
            {!loading && (
              <div className="stats-overview">
                <div className="stat-card">
                  <div className="stat-icon"><IcoUsers size={22} color="#22c55e"/></div>
                  <div className="stat-content">
                    <div className="stat-value">{employees.length}</div>
                    <div className="stat-label">Team Members</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon"><IcoFileText size={22} color="#22c55e"/></div>
                  <div className="stat-content">
                    <div className="stat-value">{thisWeekEntries.length}</div>
                    <div className="stat-label">Entries This Week</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon"><IcoClock size={22} color="#22c55e"/></div>
                  <div className="stat-content">
                    <div className="stat-value">{totalHours.toFixed(1)}h</div>
                    <div className="stat-label">Total Hours</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon"><IcoCheckCircle size={22} color="#22c55e"/></div>
                  <div className="stat-content">
                    <div className="stat-value">{pendingEntries}</div>
                    <div className="stat-label">Pending Approvals</div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Cards — matches admin style */}
            <div className="action-cards">
              <div className="action-card highlighted" onClick={() => navigate('/team')}>
                <div className="action-icon"><IcoUsers size={22} color="#22c55e"/></div>
                <div className="action-content">
                  <h3>Team Directory</h3>
                  <p>View, add and manage your team members.</p>
                  <span className="action-badge">Team Directory</span>
                </div>
                <div className="action-arrow"><IcoChevronRight size={16}/></div>
              </div>

              {features.scheduling && (
                <div className="action-card" onClick={() => navigate('/schedule')}>
                  <div className="action-icon"><IcoCalendar size={22} color="#22c55e"/></div>
                  <div className="action-content">
                    <h3>Schedule</h3>
                    <p>Build and manage your team's weekly schedule.</p>
                  </div>
                  <div className="action-arrow"><IcoChevronRight size={16}/></div>
                </div>
              )}

              <div className="action-card" onClick={() => navigate('/time-cards')}>
                <div className="action-icon"><IcoClock size={22} color="#22c55e"/></div>
                <div className="action-content">
                  <h3>Time Entry</h3>
                  <p>Submit and manage time entries for your team.</p>
                </div>
                <div className="action-arrow"><IcoChevronRight size={16}/></div>
              </div>

              {features.payroll && (
                <div className="action-card" onClick={() => navigate('/payroll')}>
                  <div className="action-icon"><IcoDollar size={22} color="#22c55e"/></div>
                  <div className="action-content">
                    <h3>Payroll</h3>
                    <p>Review and submit payroll for your department.</p>
                  </div>
                  <div className="action-arrow"><IcoChevronRight size={16}/></div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div className="right-column">

            {/* Quick Actions */}
            <div className="info-card">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <IcoZap size={18} color="#22c55e"/> Quick Actions
              </h3>
              <div className="quick-links">
                <button className="quick-link-btn" onClick={() => navigate('/employee/new')}>
                  <IcoPlus size={14}/> Add Employee
                </button>
                <button className="quick-link-btn" onClick={() => navigate('/team')}>
                  <IcoUsers size={14}/> Team Directory
                </button>
                {features.scheduling && (
                  <button className="quick-link-btn" onClick={() => navigate('/schedule')}>
                    <IcoCalendar size={14}/> Schedule
                  </button>
                )}
                {features.payroll && (
                  <button className="quick-link-btn" onClick={() => navigate('/payroll')}>
                    <IcoDollar size={14}/> Run Payroll
                  </button>
                )}
              </div>
            </div>

            {/* Recent Team Entries */}
            <div className="info-card">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <IcoClipboard size={18} color="#22c55e"/> Recent Team Entries
              </h3>
              {loading ? (
                <p style={{ color: '#6b7280' }}>Loading...</p>
              ) : entries.length === 0 ? (
                <p style={{ color: '#6b7280' }}>No entries yet</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {entries.slice(0, 5).map(entry => (
                    <div key={entry._id} style={{ padding: '0.75rem', background: 'linear-gradient(to right, #f9fafb, #f3f4f6)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: '600', color: '#111827' }}>{entry.name || 'Unknown'}</div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{entry.hoursWorked}h • {new Date(entry.date).toLocaleDateString(undefined, { timeZone: 'UTC' })}</div>
                      </div>
                      <StatusBadge status={entry.status}/>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* My Team */}
            <div className="info-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <IcoUsers size={18} color="#22c55e"/> My Team
                </h3>
                <button onClick={() => navigate('/employee/new')} style={{ padding: '0.3rem 0.75rem', background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}>
                  + Add
                </button>
              </div>
              {employees.length === 0 ? (
                <p style={{ color: '#6b7280' }}>No team members found</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {employees.map(emp => (
                    <div key={emp._id} style={{ padding: '0.6rem 0.75rem', background: '#f9fafb', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.7rem', fontWeight: '700' }}>
                          {emp.firstName?.[0]}{emp.lastName?.[0]}
                        </div>
                        <span style={{ fontWeight: '500', fontSize: '0.875rem', color: '#111827' }}>{emp.firstName} {emp.lastName}</span>
                      </div>
                      <button
                        onClick={() => navigate(`/employee/${emp._id}`)}
                        style={{ padding: '0.25rem 0.6rem', background: '#e0e7ff', color: '#4338ca', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}
                      >
                        View
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default ManagerDashboard;
